import { useEffect } from "react";
import Player from "video.js/dist/types/player";
import { IPlayerPeakInfo } from "../../../../Models/IPlayerPeakInfo";

const SAVE_INTERVAL_MS = 15000;

/** Keep a periodic resume checkpoint, leaving a little replay context on return. */
export default function useVideoProgress(
    player: Player | null,
    videoId: number | undefined,
    peak: IPlayerPeakInfo | null | undefined,
    updatePeak: ((seconds: number) => Promise<boolean>) | undefined,
    enabled: boolean = true
) {
    const savedPosition = peak?.currentPeak;
    const peakVideoId = peak?.videoId;
    const userId = peak?.userId;

    useEffect(() => {
        if (!enabled || !player || player.isDisposed() || !videoId ||
            peakVideoId !== videoId || !userId || !updatePeak ||
            savedPosition === undefined || !Number.isFinite(savedPosition)) return;

        let initialized = false;
        let confirmedPosition = Math.max(0, Math.floor(savedPosition));
        let pendingPosition: number | null = null;
        let lastAttempt = Date.now();

        const save = () => {
            if (!initialized || player.isDisposed() || player.readyState() < 1) return;
            const current = player.currentTime();
            if (typeof current !== "number" || !Number.isFinite(current) || current < 0) return;
            const position = Math.floor(current);
            if (position === pendingPosition ||
                (pendingPosition === null && position === confirmedPosition)) return;
            pendingPosition = position;
            lastAttempt = Date.now();
            // Requests remain ordered; unsuccessful checkpoints can retry on a
            // subsequent playing tick or scrub. Never flush a new position on pause/exit.
            void updatePeak(position).then((saved) => {
                if (saved) confirmedPosition = position;
            }).catch(() => {
                // A future playing tick can retry the unsaved position.
            }).finally(() => {
                if (pendingPosition === position) pendingPosition = null;
            });
        };

        const restore = () => {
            if (initialized || player.isDisposed() || player.readyState() < 1) return;
            if (savedPosition > 0) player.currentTime(savedPosition);
            initialized = true;
            lastAttempt = Date.now();
        };
        const canCheckpointPlayback = () => initialized && !player.isDisposed() &&
            !player.paused() && !player.seeking() && !player.ended();
        const onTick = () => {
            if (canCheckpointPlayback() && Date.now() - lastAttempt >= SAVE_INTERVAL_MS) save();
        };
        const onSeeked = () => {
            if (!player.isDisposed() && !player.seeking()) save();
        };

        player.on("loadedmetadata", restore);
        player.on("timeupdate", onTick);
        player.on("seeked", onSeeked);
        // The timer also covers sparse media events. A paused/ended player must
        // retain its last regular checkpoint rather than saving its final few seconds.
        const interval = window.setInterval(onTick, SAVE_INTERVAL_MS);
        restore();

        return () => {
            window.clearInterval(interval);
            player.off("loadedmetadata", restore);
            player.off("timeupdate", onTick);
            player.off("seeked", onSeeked);
        };
    }, [player, videoId, peakVideoId, userId, savedPosition, updatePeak, enabled]);
}
